import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import { useAuth } from "../../auth/useAuth";
import { can } from "../../auth/can";

import { APP_API_V1_BASE } from "../../config/api";
const API_BASE = `${APP_API_V1_BASE}/organizations`;

export default function OrganizationNotificationSettings() {
  const { activeOrg, permissions } = useAuth();
  const token = localStorage.getItem("authToken");

  const [settings, setSettings] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);

  const [newRecipient, setNewRecipient] = useState({
    name: "",
    email: "",
  });

  const headers = {
    Authorization: `Bearer ${token}`,
    ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
  };

  /* --------------------------------------------------
     Load Data
  -------------------------------------------------- */
  const loadData = async () => {
    try {
      const [settingsRes, recipientsRes] = await Promise.all([
        axios.get(`${API_BASE}/${activeOrg.id}/notification-settings`, {
          headers,
        }),
        axios.get(`${API_BASE}/${activeOrg.id}/alert-notification-recipients`, {
          headers,
        }),
      ]);

      setSettings(settingsRes.data);
      setRecipients(recipientsRes.data);
    } catch (err) {
      console.error("Failed loading notification settings", err);
    }
  };

  useEffect(() => {
    if (!activeOrg?.id) return;
    loadData();
  }, [activeOrg]);

  /* --------------------------------------------------
     Toggle Channel
  -------------------------------------------------- */
  const updateSettings = async (field, value) => {
    try {
      await axios.patch(
        `${API_BASE}/${activeOrg.id}/notification-settings`,
        { [field]: value },
        { headers },
      );
      loadData();
    } catch (err) {
      console.error("Failed updating settings", err);
    }
  };

  /* --------------------------------------------------
     Recipient Actions
  -------------------------------------------------- */
  const createRecipient = async () => {
    try {
      await axios.post(
        `${API_BASE}/${activeOrg.id}/alert-notification-recipients`,
        newRecipient,
        { headers },
      );

      setOpenDialog(false);
      setNewRecipient({ name: "", email: "" });
      loadData();
    } catch (err) {
      console.error("Failed creating recipient", err);
    }
  };

  const toggleRecipient = async (recipientId, current) => {
    try {
      await axios.patch(
        `${API_BASE}/alert-notification-recipients/${recipientId}`,
        { is_active: !current },
        { headers },
      );
      loadData();
    } catch (err) {
      console.error("Failed toggling recipient", err);
    }
  };

  /* --------------------------------------------------
     Table Columns
  -------------------------------------------------- */
  const recipientColumns = [
    { field: "name", headerName: "Name", flex: 1 },
    { field: "email", headerName: "Email", flex: 1.5 },
    {
      field: "is_active",
      headerName: "Status",
      width: 140,
      renderCell: (params) => (
        <Chip
          label={params.value ? "ACTIVE" : "INACTIVE"}
          color={params.value ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => (
        <Switch
          checked={params.row.is_active}
          onChange={() => toggleRecipient(params.row.id, params.row.is_active)}
        />
      ),
    },
  ];

  if (!settings) return null;

  return (
    <Box>
      {/* Channel Controls */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" fontWeight={600} mb={2}>
          Notification Channels
        </Typography>

        <Box display="flex" sx={{flexDirection:{xs:"column", sm:"row"}}} gap={4}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.email_enabled}
                onChange={(e) =>
                  updateSettings("email_enabled", e.target.checked)
                }
                disabled={!can(permissions, "organization.update")}
              />
            }
            label="Email Notifications"
          />

          <FormControlLabel
            control={
              <Switch
                checked={settings.sms_enabled}
                onChange={(e) =>
                  updateSettings("sms_enabled", e.target.checked)
                }
                disabled={!can(permissions, "organization.update")}
              />
            }
            label="SMS Notifications"
          />

          <FormControlLabel
            control={
              <Switch
                checked={settings.webhook_enabled}
                onChange={(e) =>
                  updateSettings("webhook_enabled", e.target.checked)
                }
                disabled={!can(permissions, "organization.update")}
              />
            }
            label="Webhook Notifications"
          />
        </Box>
      </Paper>

      {/* Recipients Section */}
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography variant="h6" fontWeight={600}>
            Notification Recipients
          </Typography>

          {can(permissions, "organization.update") && (
            <Button variant="contained" onClick={() => setOpenDialog(true)}>
              Add Recipient
            </Button>
          )}
        </Box>

        <DataGrid
          rows={recipients}
          columns={recipientColumns}
          autoHeight
          getRowId={(r) => r.id}
          disableColumnMenu
        />
      </Paper>

      {/* Add Recipient Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Add Notification Recipient</DialogTitle>

        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            margin="dense"
            value={newRecipient.name}
            onChange={(e) =>
              setNewRecipient({
                ...newRecipient,
                name: e.target.value,
              })
            }
          />

          <TextField
            fullWidth
            label="Email"
            margin="dense"
            value={newRecipient.email}
            onChange={(e) =>
              setNewRecipient({
                ...newRecipient,
                email: e.target.value,
              })
            }
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={createRecipient}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
